/**
 * Tax / Social 試算フォームを sessionStorage に保持（ブラウザタブを閉じるまで）。
 * SPA でタブを切り替えると outlet が差し替わるため、未保存だと入力が消える。
 */

const SCHEMA = 1 as const;

export type TrialFormPersist = {
  v: typeof SCHEMA;
  fields: Record<string, string>;
  /** 最後に「計算する」が成功したか */
  hasCalculated: boolean;
};

export const TAX_TRIAL_SESSION_KEY = 'memoapp_tax_trial_v1';
export const SOCIAL_TRIAL_SESSION_KEY = 'memoapp_social_trial_v1';

function collectFormAssociatedElements(form: HTMLFormElement): Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> {
  const fid = form.id;
  const set = new Set<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>();
  form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input, select, textarea').forEach((el) => set.add(el));
  if (fid) {
    document.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(`[form="${fid}"]`).forEach((el) => set.add(el));
  }
  return [...set];
}

/** フォーム内の id 付きコントロールをシリアライズ */
export function serializeTrialForm(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {};
  for (const el of collectFormAssociatedElements(form)) {
    const id = el.id;
    if (!id) continue;
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
      out[id] = el.checked ? '1' : '0';
    } else {
      out[id] = el.value;
    }
  }
  return out;
}

/** シリアライズ値をフォームに適用 */
export function applySerializedTrialForm(fields: Record<string, string>): void {
  for (const [id, val] of Object.entries(fields)) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
      el.checked = val === '1';
    } else if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
      el.value = val;
    }
  }
}

export function loadTrialForm(key: string): TrialFormPersist | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const o = JSON.parse(raw) as TrialFormPersist;
    if (o.v !== SCHEMA || typeof o.fields !== 'object' || o.fields === null) return null;
    return { v: SCHEMA, fields: o.fields, hasCalculated: Boolean(o.hasCalculated) };
  } catch {
    return null;
  }
}

export function saveTrialForm(key: string, data: TrialFormPersist): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch {
    /* 容量超過など */
  }
}

export function debounce(fn: () => void, ms: number): () => void {
  let t: ReturnType<typeof setTimeout> | undefined;
  return () => {
    if (t) clearTimeout(t);
    t = setTimeout(() => {
      t = undefined;
      fn();
    }, ms);
  };
}
